import { Fragment, useEffect, useMemo } from 'react';
import {
  MeterOnboardingStateProps,
  meterOnboardingState,
  wizardActiveStepIdState,
  wizardStartStepState,
  wizardTotalStepState,
} from '../../atoms/meterOnboardingWizardAtom';
import StepAddMeter, { STEP_ADD_EAN_ID } from './StepAddMeter';
import StepAskConsentMeter, { STEP_ASK_CONTENT_METER_ID } from './StepAskConsentMeter';
import StepAskConsentMeterConfirm, {
  STEP_ASK_CONSENT_METER_CONFIRM_ID,
} from './StepAskConsentMeterConfirm';
import StepConfirmMeter, { STEP_CONFIRM_METER_ID } from './StepConfirmMeter';
import StepConsentMeter, { STEP_CONSENT_METER_ID } from './StepConsentMeter';
import StepCustomizeMeter, { STEP_CUSTOMIZE_METER_ID } from './StepCustomizeMeter';
import StepNoAccessMeter, { STEP_NO_ACCESS_METER_ID } from './StepNoAcccessMeter';
import { UIStateProps, uiState } from '../../atoms/uiAtom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import InformationCard from '../information/InformationCard';
import InformationPanel from '../information/InformationPanel';
import Stepper from '../wizard/Stepper';
import { Wizard } from 'react-use-wizard';
import { useTranslation } from 'react-i18next';

export default function MeterOnboardingWizard() {
  const { t } = useTranslation();
  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const wizardStartStep = useRecoilValue<number>(wizardStartStepState);
  const wizardTotalStep = useRecoilValue<number>(wizardTotalStepState);
  const wizardActiveStepId = useRecoilValue<string | null>(wizardActiveStepIdState);
  const [meterOnboarding, setMeterOnboardingState] =
    useRecoilState<MeterOnboardingStateProps>(meterOnboardingState);

  const steps = useMemo(
    () => [
      {
        id: STEP_ADD_EAN_ID,
        component: <StepAddMeter />,
      },
      {
        id: STEP_CUSTOMIZE_METER_ID,
        component: <StepCustomizeMeter />,
      },
      {
        id: STEP_CONSENT_METER_ID,
        component: <StepConsentMeter />,
      },
      {
        id: STEP_CONFIRM_METER_ID,
        component: <StepConfirmMeter />,
      },
      {
        id: STEP_ASK_CONTENT_METER_ID,
        component: <StepAskConsentMeter />,
      },
      {
        id: STEP_ASK_CONSENT_METER_CONFIRM_ID,
        component: <StepAskConsentMeterConfirm />,
      },
      {
        id: STEP_NO_ACCESS_METER_ID,
        component: <StepNoAccessMeter />,
      },
    ],
    []
  );

  useEffect(() => {
    setMeterOnboardingState((state) => ({
      ...state,
      steps: steps.map((step) => ({ id: step.id })),
    }));
  }, [steps, setMeterOnboardingState]);

  const handleStepChange = (stepIndex: number) => {
    setMeterOnboardingState((state) => ({
      ...state,
      activeStepIndex: stepIndex,
    }));
    setUiState((prev) => ({ ...prev, hasError: false }));
  };

  return (
    <section className="container-fluid meter-onboarding h-100">
      <div className="row h-100 ">
        <div className="meter-onboarding__content col-12 col-md-8 col-lg-9 col-xl-10 pr-0">
          <Wizard
            onStepChange={handleStepChange}
            header={
              <Stepper activeStep={wizardStartStep} totalSteps={wizardTotalStep}>
                <div>
                  <h1>{t('step.add.meter.title')}</h1>
                  {wizardActiveStepId === STEP_ADD_EAN_ID && (
                    <h2>{t('step.add.meter.subtitle')}</h2>
                  )}
                </div>
              </Stepper>
            }
          >
            {steps.map((step) => (
              <Fragment key={step.id}>{step.component}</Fragment>
            ))}
          </Wizard>
        </div>
        <div className="col-12 col-md-4 col-lg-3 col-xl-2 h-100 ps-0 pe-0 d-none d-md-block">
          <InformationPanel>
            <Fragment>
              {wizardActiveStepId === STEP_ADD_EAN_ID && (
                <InformationCard>
                  <h1>{t('step.add.meter.ean.information.card.first')}</h1>
                  <p>{t('step.add.meter.ean.information.card.second')}</p>
                  <p>{t('step.add.meter.ean.information.card.third')}</p>
                </InformationCard>
              )}

              {wizardActiveStepId === STEP_CUSTOMIZE_METER_ID &&
                meterOnboarding.eanEligibilityOutResponse.isCapable &&
                meterOnboarding.eanEligibilityOutResponse.isSmart &&
                meterOnboarding.isAccMember === false && (
                  <InformationCard>
                    <h1>{t('step.customize.meter.tariff.information.card.first')}</h1>
                    <p>{t('step.customize.meter.tariff.information.card.second')}</p>
                    <p>{t('step.customize.meter.tariff.information.card.third')}</p>
                  </InformationCard>
                )}
            </Fragment>
          </InformationPanel>
        </div>
      </div>
    </section>
  );
}
